import { getApprovalPolicyOptions, getApprovalPolicyQueryKey, getMeQueryKey, getOrgOptions, putApprovalPolicyMutation, updateOrgMutation } from "@payezy/api-client/public/query";
import { Alert, Button, Card, CardBody, CardHeader, DescriptionList, Field, Input, PageHeader, Select, formatUSD, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { problemMessage } from "../lib/api";
import { useOrg } from "../lib/org";

export function OrgSettingsPage() {
  const org = useOrg();
  const qc = useQueryClient();
  const path = { orgId: org.org_id };
  const details = useQuery(getOrgOptions({ path }));
  const policy = useQuery(getApprovalPolicyOptions({ path }));
  const [name, setName] = useState(org.org_name);
  const [threshold, setThreshold] = useState("0");
  const [approvers, setApprovers] = useState(0);
  const [allowSelf, setAllowSelf] = useState(false);
  useEffect(() => {
    if (policy.data) {
      setThreshold(policy.data.threshold_amount);
      setApprovers(policy.data.required_approvers);
      setAllowSelf(policy.data.allow_self_approval);
    }
  }, [policy.data]);

  const rename = useMutation({
    ...updateOrgMutation(),
    onSuccess: () => { toast.success("Saved"); void qc.invalidateQueries({ queryKey: getMeQueryKey() }); },
    onError: (e) => toast.error(problemMessage(e)),
  });
  const savePolicy = useMutation({
    ...putApprovalPolicyMutation(),
    onSuccess: () => { toast.success("Approval policy updated"); void qc.invalidateQueries({ queryKey: getApprovalPolicyQueryKey({ path }) }); },
    onError: (e) => toast.error(problemMessage(e)),
  });
  const canManage = org.can("settings:manage");

  return (
    <>
      <PageHeader title="Settings" />
      <div className="grid gap-6">
        <Card>
          <CardHeader title="Organization" />
          <CardBody>
            <form className="flex items-end gap-3" onSubmit={(e) => { e.preventDefault(); rename.mutate({ path, body: { name } }); }}>
              <Field label="Display name" className="flex-1">
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} />
              </Field>
              {canManage && <Button type="submit" variant="secondary" loading={rename.isPending}>Save</Button>}
            </form>
            {details.data && (
              <div className="mt-5">
                <DescriptionList
                  items={[
                    { label: "Type", value: details.data.type === "business" ? "Business" : "Personal" },
                    { label: "Account ID", value: details.data.id, mono: true },
                    { label: "Daily payout limit", value: formatUSD(details.data.daily_payout_limit) },
                    { label: "Per-payment limit", value: formatUSD(details.data.single_payout_limit) },
                  ]}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {org.org_type === "business" && (
          <Card>
            <CardHeader title="Payment approvals" description="Require a second person to approve larger payments (maker-checker)." />
            <CardBody>
              <form
                className="grid gap-4 sm:grid-cols-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  savePolicy.mutate({ path, body: { threshold_amount: threshold || "0", required_approvers: approvers, allow_self_approval: allowSelf } });
                }}
              >
                <Field label="Approval required at or above (USD)" hint="0 with approvers set means every payment needs approval.">
                  <Input inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} disabled={!canManage} />
                </Field>
                <Field label="Approvers required">
                  <Select value={approvers} onChange={(e) => setApprovers(Number(e.target.value))} disabled={!canManage}>
                    {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n === 0 ? "No approval" : n}</option>)}
                  </Select>
                </Field>
                <Field label="Initiator can approve own payment">
                  <Select value={allowSelf ? "yes" : "no"} onChange={(e) => setAllowSelf(e.target.value === "yes")} disabled={!canManage}>
                    <option value="no">No (recommended)</option>
                    <option value="yes">Yes</option>
                  </Select>
                </Field>
                {savePolicy.isError && <Alert tone="danger" className="sm:col-span-3">{problemMessage(savePolicy.error)}</Alert>}
                {canManage && (
                  <div className="sm:col-span-3">
                    <Button type="submit" variant="secondary" loading={savePolicy.isPending}>Save policy</Button>
                  </div>
                )}
              </form>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
}
